import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserMessages,
  sendMessage,
  getMessages,
  getConversation,
  getRocketChatDMHistory,
  Message,
  MessageType,
  MessageDirection,
  MessageStatus,
  RocketChatMessage,
} from '@/api/messages.api';

/**
 * Hook to get messages for a specific user
 */
export function useUserMessages(userId: string | undefined, options?: {
  limit?: number;
  type?: MessageType;
  days?: number;
}) {
  return useQuery({
    queryKey: ['messages', 'user', userId, options],
    queryFn: () => getUserMessages(userId!, options),
    enabled: !!userId,
  });
}

/**
 * Hook to send a message to a user
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, content }: { userId: string; content: string }) =>
      sendMessage(userId, content),
    onSuccess: (_data, variables) => {
      // Invalidate user messages cache
      queryClient.invalidateQueries({ queryKey: ['messages', 'user', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

/**
 * Hook to get messages with filters
 */
export function useMessages(options?: {
  userId?: string;
  type?: MessageType;
  direction?: MessageDirection;
  status?: MessageStatus;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['messages', options],
    queryFn: () => getMessages(options),
  });
}

/**
 * Hook to get conversation messages
 */
export function useConversation(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['messages', 'conversation', conversationId],
    queryFn: () => getConversation(conversationId!),
    enabled: !!conversationId,
  });
}

/**
 * Hook to get DM history from Rocket.Chat
 */
export function useRocketChatDMHistory(username: string | undefined, count: number = 50) {
  return useQuery({
    queryKey: ['rocketchat', 'dm', username, count],
    queryFn: () => getRocketChatDMHistory(username!, count),
    enabled: !!username,
  });
}

export type { Message, MessageType, MessageDirection, MessageStatus, RocketChatMessage };
